import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import {
  SparklesIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftRightIcon,
  MegaphoneIcon,
  LightBulbIcon,
  PhoneArrowDownLeftIcon,
  PhoneArrowUpRightIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { analyticsAPI } from '../services/api';
import { displayLabel } from '../utils/format';
import { useAuthStore } from '../store/authStore';
import MetricHint from '../components/common/MetricHint';
import { useLicense } from '../hooks/useLicense';
import InsightsTabs from '../components/common/InsightsTabs';

// Synthetic demo data shown to non-Scale visitors so the page previews the
// product instead of a wall of empty states. Numbers are deliberately
// small + rounded so it's obviously a mock, not real customer data.
const DEMO_LEADS = [
  { id: 'demo-1', name: 'Preview Lead 1', campaign_source: 'whatsapp',   priority_tier: 'hot',  score: 82, created_at: new Date(Date.now() - 2*86400000).toISOString() },
  { id: 'demo-2', name: 'Preview Lead 2', campaign_source: 'google_ads', priority_tier: 'warm', score: 61, created_at: new Date(Date.now() - 5*86400000).toISOString() },
  { id: 'demo-3', name: 'Preview Lead 3', campaign_source: 'chat',       priority_tier: 'hot',  score: 88, created_at: new Date(Date.now() - 7*86400000).toISOString() },
  { id: 'demo-4', name: 'Preview Lead 4', campaign_source: 'wati',       priority_tier: 'cold', score: 34, created_at: new Date(Date.now() - 12*86400000).toISOString() },
  { id: 'demo-5', name: 'Preview Lead 5', campaign_source: 'chat',       priority_tier: 'warm', score: 67, created_at: new Date(Date.now() - 18*86400000).toISOString() },
];
const DEMO_CAMPAIGNS  = { sources: ['whatsapp', 'google_ads', 'chat', 'wati'], names: ['diwali_promo', 'summer_sale', 'organic', 'welcome_broadcast'] };
const DEMO_INSIGHTS   = { total_conversations: 128, common_intents: ['pricing questions', 'demo booking', 'callback request'], last_updated: new Date().toISOString() };
const DEMO_THEMES     = { week_anchor: new Date().toISOString(), themes: [
  { theme: 'Pricing clarity', mentions: 24, sample_query: 'What is included in the Growth plan?' },
  { theme: 'Booking friction', mentions: 18, sample_query: 'How do I book a demo for next week?' },
  { theme: 'Feature scope', mentions: 12, sample_query: 'Do you support WhatsApp for outbound campaigns?' },
]};

const INBOUND_SOURCES = new Set(['chat', 'web', 'widget', 'organic', 'inbound']);
const OUTBOUND_SOURCES = new Set(['whatsapp', 'wati', 'bsp', 'gallabox', 'interakt', 'gupshup', 'aisensy', 'twilio', 'meta', 'manual', 'outbound']);

const cardClass = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm';

function classifyDirection(source) {
  if (!source) return 'unknown';
  const s = String(source).toLowerCase();
  if (OUTBOUND_SOURCES.has(s) || s.includes('whatsapp') || s.includes('wa_')) return 'outbound';
  if (INBOUND_SOURCES.has(s) || s.includes('chat') || s.includes('widget')) return 'inbound';
  return 'unknown';
}

export default function AILeadIntelligencePage() {
  const { tenantId } = useParams();
  const currentUser = useAuthStore((state) => state.user);
  const { hasFeature, isLoading: licenseLoading } = useLicense();
  const isSuperadmin = currentUser?.role === 'superadmin' || currentUser?.is_superuser;
  // Preview mode: below-Scale, non-superadmin visitors see synthetic demo data
  // with a Scale-upgrade banner. Backend also 403s the campaign brief endpoint
  // for these tiers, but the rest of the read-only stats endpoints run in
  // demo mode purely on the client — no API round-trip needed.
  const previewMode = !licenseLoading && !hasFeature('ai_insights') && !isSuperadmin;
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState({ sources: [], names: [] });
  const [scoreTrends, setScoreTrends] = useState({ buckets: [], summary: null });
  const [trendsPage, setTrendsPage] = useState(1);
  const TRENDS_PAGE_SIZE = 20;
  const [insightsSummary, setInsightsSummary] = useState(null);
  const [intentThemes, setIntentThemes] = useState({ week_anchor: null, themes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [briefModalOpen, setBriefModalOpen] = useState(false);
  const [briefCampaign, setBriefCampaign] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefData, setBriefData] = useState(null);
  const [briefError, setBriefError] = useState(null);

  useEffect(() => {
    // Preview mode short-circuits the API calls (backend 403s campaign-brief
    // anyway) and paints the page with synthetic demo data so the visitor
    // sees a shaped product, not an empty shell.
    if (previewMode) {
      setLeads(DEMO_LEADS);
      setCampaigns(DEMO_CAMPAIGNS);
      setInsightsSummary(DEMO_INSIGHTS);
      setIntentThemes(DEMO_THEMES);
      setLoading(false);
      return;
    }
    const tenantParams = tenantId ? { tenant_id: tenantId } : {};
    let cancelled = false;
    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);
        const [leadsRes, campaignsRes, trendsRes, insightsRes, themesRes] = await Promise.all([
          analyticsAPI.getLeads({ ...tenantParams, days: 90, limit: 500 }),
          analyticsAPI.getLeadCampaigns(tenantParams),
          analyticsAPI.getScoreTrends({ ...tenantParams, days: 90, group_by: 'campaign_source' }),
          analyticsAPI.getConversationInsightsSummary({ ...tenantParams, days: 30 }).catch(() => ({ data: null })),
          analyticsAPI.getIntentThemes({ ...tenantParams, limit: 10 }).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setLeads(leadsRes.data?.leads || []);
        setCampaigns({
          sources: campaignsRes.data?.sources || [],
          names: campaignsRes.data?.names || [],
        });
        setScoreTrends({
          buckets: trendsRes.data?.buckets || [],
          summary: trendsRes.data?.summary || null,
        });
        setInsightsSummary(insightsRes.data || null);
        setIntentThemes({
          week_anchor: themesRes.data?.week_anchor || null,
          themes: themesRes.data?.themes || [],
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Lead intelligence load failed:', err);
        setError(err?.response?.data?.detail || err.message || 'Failed to load lead intelligence data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [tenantId, previewMode]);

  const handleGenerateBrief = async (campaignSource) => {
    // In preview mode, don't hit the backend (would 403 anyway) — pop a
    // friendly modal explaining the feature needs AI Insights and how to add it.
    if (previewMode) {
      setBriefModalOpen(true);
      setBriefCampaign(campaignSource);
      setBriefLoading(false);
      setBriefData(null);
      setBriefError('AI campaign briefs are part of the AI Insights add-on (included in Scale). Add it from Billing or talk to us.');
      return;
    }
    setBriefModalOpen(true);
    setBriefCampaign(campaignSource);
    setBriefLoading(true);
    setBriefData(null);
    setBriefError(null);
    try {
      const tenantParams = tenantId ? { tenant_id: tenantId } : {};
      const res = await analyticsAPI.generateCampaignBrief(
        { campaign_source: campaignSource, days: 90 },
        tenantParams,
      );
      setBriefData(res.data);
    } catch (err) {
      console.error('Campaign brief failed:', err);
      setBriefError(err?.response?.data?.detail || err.message || 'Failed to generate brief');
    } finally {
      setBriefLoading(false);
    }
  };

  const handleDownloadBriefPdf = () => {
    if (!briefData) return;
    const { campaign_source, days, metrics, ai, top_leads, generated_at } = briefData;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 50;
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxLineWidth = pageWidth - margin * 2;
    let y = margin;

    const writeHeading = (text, size = 16) => {
      doc.setFont(undefined, 'bold');
      doc.setFontSize(size);
      doc.text(text, margin, y);
      y += size + 6;
      doc.setFont(undefined, 'normal');
    };
    const writePara = (text, size = 11) => {
      doc.setFontSize(size);
      const lines = doc.splitTextToSize(text || '—', maxLineWidth);
      for (const line of lines) {
        if (y > 780) { doc.addPage(); y = margin; }
        doc.text(line, margin, y);
        y += size + 3;
      }
      y += 4;
    };

    writeHeading(`Campaign Brief — ${campaign_source}`, 18);
    doc.setFontSize(10);
    doc.text(`Last ${days} days · Generated ${new Date(generated_at).toLocaleString()}`, margin, y);
    y += 18;

    writeHeading('Metrics', 13);
    writePara(
      `Total leads: ${metrics.total}   Converted: ${metrics.converted} (${metrics.conversion_pct}%)\n` +
      `Hot: ${metrics.hot}   Warm: ${metrics.warm}   Cold: ${metrics.cold}   Unscored: ${metrics.unscored}\n` +
      `Avg priority score: ${metrics.avg_score ?? '—'}\n` +
      `First seen: ${metrics.first_seen || '—'}   Last seen: ${metrics.last_seen || '—'}`
    );

    writeHeading('Summary', 13);
    writePara(ai.summary);

    writeHeading('What worked', 13);
    writePara(ai.what_worked);

    writeHeading("What didn't", 13);
    writePara(ai.what_didnt);

    writeHeading('Next actions', 13);
    (ai.next_actions || []).forEach((action, i) => writePara(`${i + 1}. ${action}`));

    if (top_leads && top_leads.length > 0) {
      writeHeading('Top leads in this campaign', 13);
      top_leads.forEach((lead) => {
        writePara(
          `${lead.name} — ${lead.priority_tier} (${lead.priority_score ?? '?'}/100)\n` +
          `Phone: ${lead.phone}   Status: ${lead.status}`
        );
      });
    }

    doc.setFontSize(8);
    doc.text(
      ai.generated ? `AI-generated (${ai.model}). Treat as guidance, not verdict.` : 'Generated from metrics only (LLM unavailable).',
      margin,
      820,
    );

    const safeCampaign = (campaign_source || 'campaign').replace(/[^a-z0-9-_]/gi, '_');
    doc.save(`serviti_campaign_brief_${safeCampaign}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const campaignPerformance = useMemo(() => {
    const byCampaign = new Map();
    for (const lead of leads) {
      const key = lead.campaign_source || 'unattributed';
      if (!byCampaign.has(key)) {
        byCampaign.set(key, { source: key, total: 0, converted: 0, contacted: 0, lost: 0 });
      }
      const row = byCampaign.get(key);
      row.total += 1;
      if (lead.status === 'converted') row.converted += 1;
      else if (lead.status === 'contacted' || lead.status === 'qualified') row.contacted += 1;
      else if (lead.status === 'lost') row.lost += 1;
    }
    return Array.from(byCampaign.values())
      .map((r) => ({
        ...r,
        conversionPct: r.total > 0 ? Math.round((r.converted / r.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [leads]);

  const directionMix = useMemo(() => {
    const mix = { inbound: 0, outbound: 0, unknown: 0 };
    for (const lead of leads) mix[classifyDirection(lead.source)] += 1;
    const total = mix.inbound + mix.outbound + mix.unknown || 1;
    return {
      ...mix,
      total,
      inboundPct: Math.round((mix.inbound / total) * 100),
      outboundPct: Math.round((mix.outbound / total) * 100),
      unknownPct: Math.round((mix.unknown / total) * 100),
    };
  }, [leads]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4 sm:space-y-6">
      <InsightsTabs />
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <SparklesIcon className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
            AI Lead Intelligence
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            AI-powered insights across every lead, conversation, and campaign — inbound and outbound.
          </p>
        </div>
        <span className="text-xs font-semibold px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
          {previewMode ? 'Preview mode' : 'New product'}
        </span>
      </div>

      {/* Scale upgrade banner - shown only in preview mode. Data shown below
          is synthetic sample data, not this tenant's real leads. */}
      {previewMode && (
        <div className="rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-4 sm:p-5 shadow-md">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide opacity-80">Preview mode</p>
              <p className="mt-1 text-lg font-bold">AI Lead Intelligence is part of the AI Insights add-on (included in Scale)</p>
              <p className="mt-1 text-sm opacity-90 max-w-2xl">
                The numbers, campaigns, insights and intent themes below are sample data to show what your view will look like. Add AI Insights to see real leads and generate AI campaign briefs from your own data.
              </p>
            </div>
            <a
              href="mailto:sales@serviti.ai?subject=Upgrade%20to%20Scale%20-%20AI%20Lead%20Intelligence"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white text-indigo-700 font-semibold rounded-lg text-sm hover:bg-indigo-50 transition whitespace-nowrap"
            >
              Talk to sales
            </a>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
            Leads (last 90d)
            <MetricHint text="All leads captured in the last 90 days across every channel and source (web widget, WhatsApp, QR, manual, etc.)." />
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{leads.length}</p>
        </div>
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
            Campaigns active
            <MetricHint text="Distinct UTM campaign sources that captured at least one lead in the window. Excludes 'unattributed' (leads with no source tag)." />
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
            {campaigns.sources.filter((s) => s && s !== 'unattributed').length}
          </p>
        </div>
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
            Inbound vs outbound
            <MetricHint text="Inbound = visitors who came via your website/widget. Outbound = leads created via WhatsApp/BSP or manual entry. A healthy mix tells you which channel is doing the work." />
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
            {directionMix.inboundPct}% / {directionMix.outboundPct}%
          </p>
        </div>
        <div className={`${cardClass} p-4`}>
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1">
            Conversion rate
            <MetricHint text="Percentage of leads your team has marked status=converted. Doesn't auto-detect — requires manual marking in the Leads page. 0% with healthy lead quality usually means follow-up gap, not lead gap." />
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
            {(() => {
              const total = leads.length || 1;
              const converted = leads.filter((l) => l.status === 'converted').length;
              return `${Math.round((converted / total) * 100)}%`;
            })()}
          </p>
        </div>
      </div>

      {/* Campaign performance */}
      <div className={cardClass}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <MegaphoneIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Campaign Performance</h2>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <p className="p-5 text-sm text-gray-400">Loading campaigns…</p>
          ) : campaignPerformance.length === 0 ? (
            <p className="p-5 text-sm text-gray-400">No leads in the last 90 days to analyze.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/30">
                <tr>
                  <th className="px-5 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Campaign source</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Leads</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Contacted</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Converted</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      Conv. %
                      <MetricHint text="Converted / total. Green ≥20% (great), amber 5-19% (typical), grey <5% (follow-up gap or low intent)." side="left" />
                    </span>
                  </th>
                  <th className="px-5 py-2 text-right font-medium text-gray-500 dark:text-gray-400">AI brief</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {campaignPerformance.map((row) => (
                  <tr key={row.source}>
                    <td className="px-5 py-2 text-gray-900 dark:text-white">{row.source}</td>
                    <td className="px-5 py-2 text-right text-gray-900 dark:text-white">{row.total}</td>
                    <td className="px-5 py-2 text-right text-gray-700 dark:text-gray-300">{row.contacted}</td>
                    <td className="px-5 py-2 text-right text-gray-700 dark:text-gray-300">{row.converted}</td>
                    <td className="px-5 py-2 text-right">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        row.conversionPct >= 20
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : row.conversionPct >= 5
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {row.conversionPct}%
                      </span>
                    </td>
                    <td className="px-5 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => handleGenerateBrief(row.source)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/40"
                      >
                        <DocumentArrowDownIcon className="h-3.5 w-3.5" />
                        Brief
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Inbound vs Outbound mix */}
      <div className={cardClass}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <ChartBarIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Inbound vs Outbound Mix</h2>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <PhoneArrowDownLeftIcon className="h-4 w-4 text-blue-500" />
                Inbound (web chat, widget)
              </span>
              <span className="text-gray-900 dark:text-white font-medium">{directionMix.inbound} ({directionMix.inboundPct}%)</span>
            </div>
            <div className="h-2 mt-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: `${directionMix.inboundPct}%` }} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <PhoneArrowUpRightIcon className="h-4 w-4 text-green-500" />
                Outbound (WhatsApp/BSP, manual)
              </span>
              <span className="text-gray-900 dark:text-white font-medium">{directionMix.outbound} ({directionMix.outboundPct}%)</span>
            </div>
            <div className="h-2 mt-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div className="h-full bg-green-500" style={{ width: `${directionMix.outboundPct}%` }} />
            </div>
          </div>
          {directionMix.unknown > 0 && (
            <div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">Other / unclassified</span>
                <span className="text-gray-900 dark:text-white font-medium">{directionMix.unknown} ({directionMix.unknownPct}%)</span>
              </div>
              <div className="h-2 mt-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div className="h-full bg-gray-400" style={{ width: `${directionMix.unknownPct}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lead Quality Scoring Trends — weekly tier breakdown per campaign */}
      <div className={cardClass}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <ArrowTrendingUpIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Quality Scoring Trends</h2>
          <MetricHint text="AI scores every lead 1-100 after their conversation ends. Hot 70-100, Warm 40-69, Cold 1-39. Unscored = conversation too short or LLM call failed. Trends are bucketed by week and campaign so you can spot quality shifts." />
          {scoreTrends.summary && (
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              {scoreTrends.summary.total} leads · {Math.max(0, scoreTrends.summary.total - (scoreTrends.summary.unscored || 0))} scored · average score of scored leads {scoreTrends.summary.avg_score ?? '—'}/100
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <p className="p-5 text-sm text-gray-400">Loading score trends…</p>
          ) : scoreTrends.buckets.length === 0 ? (
            <p className="p-5 text-sm text-gray-400">No scored leads in the last 90 days yet.</p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/30">
                <tr>
                  <th className="px-5 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Week</th>
                  <th className="px-5 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Campaign</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Total</th>
                  <th className="px-5 py-2 text-right font-medium text-red-600 dark:text-red-400">Hot</th>
                  <th className="px-5 py-2 text-right font-medium text-amber-600 dark:text-amber-400">Warm</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Cold</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-400">Unscored</th>
                  <th className="px-5 py-2 text-right font-medium text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      Avg score
                      <MetricHint text="Average AI priority score (1-100) across scored leads in this week+campaign. Unscored leads don't drag the average down." side="left" />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {scoreTrends.buckets
                  .slice((trendsPage - 1) * TRENDS_PAGE_SIZE, trendsPage * TRENDS_PAGE_SIZE)
                  .map((b, idx) => (
                  <tr key={`${b.week_start}_${b.campaign_source}_${idx}`}>
                    <td className="px-5 py-2 text-gray-900 dark:text-white whitespace-nowrap">{b.week_start}</td>
                    <td className="px-5 py-2 text-gray-700 dark:text-gray-300">{b.campaign_source}</td>
                    <td className="px-5 py-2 text-right text-gray-900 dark:text-white">{b.total}</td>
                    <td className="px-5 py-2 text-right text-red-600 dark:text-red-400 font-medium">{b.hot}</td>
                    <td className="px-5 py-2 text-right text-amber-600 dark:text-amber-400">{b.warm}</td>
                    <td className="px-5 py-2 text-right text-gray-600 dark:text-gray-400">{b.cold}</td>
                    <td className="px-5 py-2 text-right text-gray-400">{b.unscored}</td>
                    <td className="px-5 py-2 text-right text-gray-900 dark:text-white">{b.avg_score ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {scoreTrends.buckets.length > TRENDS_PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 text-sm">
            <span className="text-gray-500 dark:text-gray-400">
              Page {trendsPage} of {Math.ceil(scoreTrends.buckets.length / TRENDS_PAGE_SIZE)} ·
              {' '}{scoreTrends.buckets.length} rows
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setTrendsPage((p) => Math.max(1, p - 1))}
                disabled={trendsPage === 1}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setTrendsPage((p) =>
                  Math.min(Math.ceil(scoreTrends.buckets.length / TRENDS_PAGE_SIZE), p + 1)
                )}
                disabled={trendsPage >= Math.ceil(scoreTrends.buckets.length / TRENDS_PAGE_SIZE)}
                className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cross-channel Conversation Insights (AI Insights, processed nightly) */}
      <div className={cardClass}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Cross-channel Conversation Insights</h2>
          {insightsSummary && (
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              {insightsSummary.total_insights} conversations analysed by AI · last {insightsSummary.days} days
            </span>
          )}
        </div>
        <div className="p-5">
          {loading ? (
            <p className="text-sm text-gray-400">Loading insights…</p>
          ) : !insightsSummary || insightsSummary.total_insights === 0 ? (
            <p className="text-sm text-gray-400">
              No insights yet. The nightly job populates this section once AI Insights is active — first results land within 24 hours.
            </p>
          ) : (
            <div className="space-y-5">
              {/* Outcome funnel bars */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">How conversations ended</h3>
                {['lead_captured', 'escalated', 'dropped_qualification', 'abandoned', 'bounced', 'other'].map((outcome) => {
                  const count = insightsSummary.outcomes?.[outcome] || 0;
                  const pct = insightsSummary.total_insights
                    ? Math.round((count / insightsSummary.total_insights) * 100)
                    : 0;
                  const color = {
                    lead_captured: 'bg-green-500',
                    escalated: 'bg-indigo-500',
                    dropped_qualification: 'bg-amber-500',
                    abandoned: 'bg-gray-400',
                    bounced: 'bg-red-500',
                    other: 'bg-gray-300',
                  }[outcome];
                  return (
                    <div key={outcome} className="mb-2">
                      <div className="flex items-center justify-between text-xs text-gray-700 dark:text-gray-300">
                        <span>{displayLabel(outcome)}</span>
                        <span>{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 mt-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-agent comparison */}
              {insightsSummary.agents && insightsSummary.agents.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Agent performance</h3>
                  <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-xs text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="text-left py-1">Agent</th>
                        <th className="text-right py-1">Conversations</th>
                        <th className="text-right py-1">Captured</th>
                        <th className="text-right py-1">Capture rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {insightsSummary.agents.slice(0, 10).map((a) => (
                        <tr key={a.agent}>
                          <td className="py-1 text-gray-900 dark:text-white">{a.agent}</td>
                          <td className="py-1 text-right text-gray-700 dark:text-gray-300">{a.total}</td>
                          <td className="py-1 text-right text-green-600 dark:text-green-400">{a.lead_captured}</td>
                          <td className="py-1 text-right text-gray-900 dark:text-white">{a.capture_rate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {/* Drop-off samples */}
              {insightsSummary.drop_off_samples && insightsSummary.drop_off_samples.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Recent drop-off moments</h3>
                  <ul className="space-y-2">
                    {insightsSummary.drop_off_samples.slice(0, 5).map((s, i) => (
                      <li key={i} className="text-xs text-gray-700 dark:text-gray-300 border-l-2 border-amber-400 pl-3">
                        <span className="font-medium">{s.marker}</span>
                        {s.summary && <span className="text-gray-500"> — {s.summary}</span>}
                        <span className="block text-[10px] text-gray-400 mt-0.5">via {s.source_type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI-Extracted Intent Themes (AI Insights, refreshed weekly) */}
      <div className={cardClass}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <LightBulbIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI-Extracted Intent Themes</h2>
          {intentThemes.week_anchor && (
            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
              Week of {intentThemes.week_anchor}
            </span>
          )}
        </div>
        <div className="p-5">
          {loading ? (
            <p className="text-sm text-gray-400">Loading themes…</p>
          ) : intentThemes.themes.length === 0 ? (
            <p className="text-sm text-gray-400">
              No themes generated yet. The weekly job clusters last-30d customer messages into 5-10 themes — first results land on the Monday after AI Insights starts.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {intentThemes.themes.map((t) => (
                <div key={t.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t.theme_name}</h3>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                      {t.message_count}
                    </span>
                  </div>
                  {t.description && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t.description}</p>
                  )}
                  {t.sample_messages && t.sample_messages.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {t.sample_messages.slice(0, 3).map((s, i) => (
                        <li key={i} className="text-xs text-gray-500 dark:text-gray-400 italic border-l-2 border-gray-300 dark:border-gray-600 pl-2">
                          "{s}"
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Loaded {leads.length} leads from the last 90 days{currentUser?.tenant_id ? '' : tenantId ? ` for tenant ${tenantId}` : ''}.
      </p>

      {/* AI Campaign Brief modal */}
      {briefModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <DocumentArrowDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                AI Campaign Brief — {briefCampaign}
              </h3>
              <button
                type="button"
                onClick={() => setBriefModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {briefLoading && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Generating brief… this takes a few seconds.</p>
              )}
              {briefError && (
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
                  {briefError}
                </div>
              )}
              {briefData && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className={`${cardClass} p-3`}>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        Total leads
                        <MetricHint text="All leads from this campaign source in the selected window." />
                      </p>
                      <p className="font-bold text-gray-900 dark:text-white">{briefData.metrics.total}</p>
                    </div>
                    <div className={`${cardClass} p-3`}>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        Converted
                        <MetricHint text="Leads marked status=converted by your team, with the % of total in brackets. Requires manual marking — not auto-detected." />
                      </p>
                      <p className="font-bold text-gray-900 dark:text-white">
                        {briefData.metrics.converted} ({briefData.metrics.conversion_pct}%)
                      </p>
                    </div>
                    <div className={`${cardClass} p-3`}>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        Hot leads
                        <MetricHint text="Leads scored 70-100 by the AI based on conversation intent signals (pricing questions, budget mentions, appointment requests, etc.)." />
                      </p>
                      <p className="font-bold text-red-600 dark:text-red-400">{briefData.metrics.hot}</p>
                    </div>
                    <div className={`${cardClass} p-3`}>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        Avg score
                        <MetricHint text="Average AI priority score (1-100) across scored leads. Hot 70+, Warm 40-69, Cold 1-39. Score reflects intent quality, not message length or politeness. Tip: a healthy avg score with 0% conversion usually means follow-up gap, not lead quality." side="left" />
                      </p>
                      <p className="font-bold text-gray-900 dark:text-white">{briefData.metrics.avg_score ?? '—'}</p>
                    </div>
                  </div>

                  <section>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Summary</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{briefData.ai.summary}</p>
                  </section>
                  <section>
                    <h4 className="text-sm font-semibold text-green-700 dark:text-green-400">What worked</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{briefData.ai.what_worked}</p>
                  </section>
                  <section>
                    <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-400">What didn't</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 whitespace-pre-wrap">{briefData.ai.what_didnt}</p>
                  </section>
                  <section>
                    <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">Next actions</h4>
                    <ol className="text-sm text-gray-700 dark:text-gray-300 mt-1 space-y-1 list-decimal pl-5">
                      {(briefData.ai.next_actions || []).map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ol>
                  </section>
                  {!briefData.ai.generated && (
                    <p className="text-xs text-gray-400 italic">
                      AI generation unavailable — this brief was built from metrics alone.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 p-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setBriefModalOpen(false)}
                className="px-3 py-1.5 rounded text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Close
              </button>
              <button
                type="button"
                disabled={!briefData}
                onClick={handleDownloadBriefPdf}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
